import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlaylists } from '@/context/PlaylistContext';
import { formatDate } from '@/utils/formatters';
import { ArrowLeft, Plus, Trash2, Music } from 'lucide-react';

export default function PlaylistsPage() {
  const { playlists, loading, createPlaylist, deletePlaylist } = usePlaylists();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');

  async function handleCreatePlaylist(e: React.FormEvent) {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;

    await createPlaylist(newPlaylistName);
    setNewPlaylistName('');
    setShowCreateModal(false);
  }

  if (loading) {
    return <div className="text-center text-white p-8">Loading playlists...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-white">
            <ArrowLeft size={20} />
            Back to Songs
          </Link>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Create Playlist
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">My Playlists</h1>

        {playlists.length === 0 ? (
          <div className="text-center text-text-secondary py-12">
            <Music size={48} className="mx-auto mb-4 opacity-50" />
            <p>No playlists yet. Create one to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {playlists.map((playlist) => (
              <div key={playlist.id} className="card group">
                <div className="flex items-start justify-between mb-3">
                  <Link to={`/playlists/${playlist.id}`} className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-primary transition-colors">
                      {playlist.name}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {playlist.songs.length} track{playlist.songs.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-text-muted">
                      Created {formatDate(playlist.createdAt.toString())}
                    </p>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      deletePlaylist(playlist.id);
                    }}
                    className="text-text-secondary hover:text-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {playlist.songs.length > 0 && (
                  <div className="border-t border-border pt-3 space-y-1">
                    {playlist.songs.slice(0, 3).map((song, index) => (
                      <p key={index} className="text-sm text-text-secondary truncate">
                        {song.revisionName ? `${song.songName} - ${song.revisionName}` : song.songName}
                      </p>
                    ))}
                    {playlist.songs.length > 3 && (
                      <p className="text-xs text-text-muted">
                        +{playlist.songs.length - 3} more
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Playlist Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Create Playlist</h2>
            <form onSubmit={handleCreatePlaylist}>
              <input
                type="text"
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                placeholder="Playlist name"
                className="input mb-4"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewPlaylistName('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
